import { useCallback, useEffect, useRef, useState } from "react";

export const useCarousel = (payload: { cardWidth: number; gap: number; totalPages: number; interval: number }) => {
    const { cardWidth, gap, totalPages, interval } = payload;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    // Swipe detection
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const isDraggingRef = useRef(false);

    // Handle swipe gestures
    const handleTouchStart = useCallback((e: TouchEvent) => {
        startXRef.current = e.touches[0].clientX;
        startYRef.current = e.touches[0].clientY;
        isDraggingRef.current = true;
    }, []);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!isDraggingRef.current) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = startXRef.current - currentX;
        const diffY = startYRef.current - currentY;

        // Only prevent default for horizontal swipes to avoid page scrolling
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 20) {
            e.preventDefault();
        }
    }, []);

    const handleTouchEnd = useCallback(
        (e: TouchEvent) => {
            if (!isDraggingRef.current) return;

            const currentX = e.changedTouches[0].clientX;
            const diffX = startXRef.current - currentX;
            const threshold = 30; // Reduced threshold for better responsiveness

            if (Math.abs(diffX) > threshold) {
                if (diffX > 0) {
                    // Swipe left - go to next page
                    const nextPage = Math.min(currentPage + 1, totalPages - 1);
                    setCurrentPage(nextPage);
                    if (containerRef.current) {
                        containerRef.current.scrollTo({
                            left: nextPage * (cardWidth + gap),
                            behavior: "smooth",
                        });
                    }
                } else {
                    // Swipe right - go to previous page
                    const prevPage = Math.max(currentPage - 1, 0);
                    setCurrentPage(prevPage);
                    if (containerRef.current) {
                        containerRef.current.scrollTo({
                            left: prevPage * (cardWidth + gap),
                            behavior: "smooth",
                        });
                    }
                }
            }

            isDraggingRef.current = false;
        },
        [currentPage, totalPages, cardWidth, gap],
    );

    // Add swipe event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Touch events
        container.addEventListener("touchstart", handleTouchStart, { passive: false });
        container.addEventListener("touchmove", handleTouchMove, { passive: false });
        container.addEventListener("touchend", handleTouchEnd, { passive: false });

        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Auto scroll
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const timer = setInterval(() => {
            setCurrentPage((prev) => {
                const nextPage = (prev + 1) % totalPages;
                const scrollLeft = nextPage * (cardWidth + gap);
                container.scrollTo({
                    left: scrollLeft,
                    behavior: "smooth",
                });
                return nextPage;
            });
        }, interval);

        return () => clearInterval(timer);
    }, [totalPages, interval, cardWidth, gap]);

    return { containerRef, currentPage, totalPages };
};
